/**
 * The resumable draft: a fully-serializable snapshot of a parsed + mapped
 * dataset that survives the M7 save/signup interstitial with no re-upload
 * (criterion 11). It holds only structured, geocode-relevant data — never raw
 * file bytes (R-5 privacy posture). `draft_id` is minted server-side; the body
 * lives client-side (IndexedDB, draftStore.ts).
 */

import { reduceStack } from "@/lib/nl/opstack";
import type { MapOp, SavedView } from "@/lib/contracts/nl";
import type { MapSpec } from "@/lib/map/mapspec";
import type { ColumnMapping, ColumnProfile, IngestDraft, IngestSource, ParsedRow } from "./types";

export interface MakeDraftParams {
  draft_id: string;
  /** User-facing map name carried through auth recovery. */
  title?: string;
  /** Whether the first-save naming prompt has already been confirmed. */
  title_confirmed?: boolean;
  source: IngestSource;
  profiles: ColumnProfile[];
  mapping: ColumnMapping;
  rows: ParsedRow[];
  country_default: string;
  /** ISO timestamp; injectable for deterministic tests. */
  created_at?: string;
  /** M5 — the GUI-authored base spec beneath the NL op stack. */
  spec?: MapSpec;
  /** M5 — the ordered NL op stack (rendered = reduceStack(spec, nl_ops)). */
  nl_ops?: MapOp[];
  /** M5 — promoted saved views (AC-10). */
  saved_views?: SavedView[];
}

export function makeDraft(p: MakeDraftParams): IngestDraft {
  return {
    draft_id: p.draft_id,
    ...(p.title ? { title: p.title } : {}),
    ...(p.title_confirmed ? { title_confirmed: true } : {}),
    source: p.source,
    profiles: p.profiles,
    mapping: p.mapping,
    rows: p.rows,
    country_default: p.country_default,
    created_at: p.created_at ?? new Date().toISOString(),
    ...(p.spec ? { spec: p.spec } : {}),
    ...(p.nl_ops && p.nl_ops.length > 0 ? { nl_ops: p.nl_ops } : {}),
    ...(p.saved_views && p.saved_views.length > 0 ? { saved_views: p.saved_views } : {}),
  };
}

/**
 * Reproduce a reloaded draft's rendered style by folding its persisted NL op
 * stack onto its base spec — a PURE reducer, ZERO /api/nl calls (AC-2). Returns
 * null for a draft with no rendered-map state (nothing to restore).
 */
export function renderedSpecFromDraft(draft: IngestDraft): MapSpec | null {
  if (!draft.spec) return null;
  return draft.nl_ops && draft.nl_ops.length > 0 ? reduceStack(draft.spec, draft.nl_ops) : draft.spec;
}

/** A draft must survive JSON/structured-clone round-trips to resume cleanly. */
export function isSerializableDraft(draft: IngestDraft): boolean {
  try {
    const clone = JSON.parse(JSON.stringify(draft)) as IngestDraft;
    return clone.draft_id === draft.draft_id && clone.rows.length === draft.rows.length;
  } catch {
    return false;
  }
}
